import { IconButton, InputAdornment, TextField } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatScriptureRefs,
  parseScriptureRefs,
  type ScriptureRef,
} from '../../../lib/scripture-refs';

// ─── «Textos clave» de una sesión de la plantilla (S2) ──────────────────────
// Antes las citas se cargaban (importación, copia) y salían en el PDF, pero el
// editor no las mostraba. Se editan como texto con la notación del PDF; cada
// cambio VÁLIDO se aplica al instante; uno inválido muestra el error y no toca
// lo guardado. Vaciar el campo quita los textos clave de la sesión.

export function SessionKeyTextsField({
  value,
  onChange,
}: {
  value: ScriptureRef[] | undefined;
  /** `undefined` = la sesión queda sin textos clave. */
  onChange: (refs: ScriptureRef[] | undefined) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const k = 'templateEditor.modules.sessions';
  const saved = formatScriptureRefs(value);
  const [draft, setDraft] = useState(saved);
  const parsed = parseScriptureRefs(draft);

  // Si lo guardado cambia por fuera (copia, deshacer), el campo lo refleja; si
  // es lo mismo que se está escribiendo con otro espaciado, no se pisa.
  useEffect(() => {
    const current = parseScriptureRefs(draft);
    if (!current.ok || formatScriptureRefs(current.refs) !== saved) setDraft(saved);
  }, [saved]);

  const apply = (text: string) => {
    setDraft(text);
    const r = parseScriptureRefs(text);
    if (r.ok) onChange(r.refs.length > 0 ? r.refs : undefined);
  };

  const error = parsed.ok
    ? null
    : parsed.error === 'too_many'
      ? t(`${k}.keyTextsTooMany`, { max: parsed.max })
      : t(`${k}.keyTextsInvalid`, { part: parsed.part });

  return (
    <TextField
      size="small"
      label={t(`${k}.keyTexts`)}
      placeholder={t(`${k}.keyTextsPlaceholder`)}
      value={draft}
      onChange={(e) => apply(e.target.value)}
      title={t(`${k}.keyTextsHelp`)}
      error={!!error}
      helperText={error ?? undefined}
      sx={{ flex: 1 }}
      InputProps={{
        endAdornment: draft ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              aria-label={t(`${k}.keyTextsClear`)}
              title={t(`${k}.keyTextsClear`)}
              onClick={() => apply('')}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
